import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { Bookmark, BookmarkCheck, ExternalLink } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import api from '../lib/api';
import { toast } from 'sonner';
import MentionText from './MentionText';

const CATEGORIES = [
  { value: 'fees_invoice', label: 'Overdue', color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  { value: 'partially_paid', label: 'Partially Paid', color: 'bg-yellow-50 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' },
  { value: 'paid', label: 'Paid', color: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500' },
  { value: 'attendance', label: 'Attendance', color: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  { value: 'placement', label: 'Placement', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  { value: 'defaulter', label: 'Defaulter', color: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  { value: 'refund', label: 'Refund', color: 'bg-purple-50 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  { value: 'credit_note', label: 'Credit Note', color: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
  { value: 'batch_pending', label: 'Batch Pending Allocation', color: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  { value: 'batch_on_hold', label: 'Batch On Hold', color: 'bg-slate-100 text-slate-700 border-slate-300', dot: 'bg-slate-500' },
  { value: 'dropout', label: 'Dropout', color: 'bg-cyan-50 text-cyan-700 border-cyan-200', dot: 'bg-cyan-500' },
  { value: 'blacklisted', label: 'Blacklisted', color: 'bg-zinc-900 text-white border-zinc-900', dot: 'bg-zinc-900' },
  { value: 'no_crm', label: 'No CRM', color: 'bg-amber-100 text-amber-800 border-amber-300', dot: 'bg-amber-600' },
];

const PRIORITIES = [
  { value: 'high', label: 'High', color: 'bg-red-100 text-red-700' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-600' },
];

const catMap = {};
CATEGORIES.forEach(c => { catMap[c.value] = c; });
catMap['issue'] = { value: 'issue', label: 'Issue', color: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' };
const prioMap = {};
PRIORITIES.forEach(p => { prioMap[p.value] = p; });

// Normalize category: always return array
function normCats(category) {
  if (!category) return [];
  if (Array.isArray(category)) return category;
  return [category];
}

// Get first category meta for display
function firstCatMeta(category) {
  const cats = normCats(category);
  return cats.length > 0 ? (catMap[cats[0]] || catMap.fees_invoice) : catMap.fees_invoice;
}

// ── Context for bookmark map ──
const BookmarkMapContext = createContext({ map: {}, refresh: () => {} });

export function BookmarkMapProvider({ children }) {
  const [map, setMap] = useState({});
  const refresh = useCallback(() => {
    api.get('/training/audit-bookmarks/bookmarked-map')
      .then(r => setMap(r.data.map || {}))
      .catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return (
    <BookmarkMapContext.Provider value={{ map, refresh }}>
      {children}
    </BookmarkMapContext.Provider>
  );
}

export function useBookmarkMap() {
  return useContext(BookmarkMapContext);
}

// ── Inline Bookmark Icon ──
export function BookmarkInline({ student }) {
  const { map, refresh } = useBookmarkMap();
  const [showCreate, setShowCreate] = useState(false);
  const bookmarkInfo = map[student.enrollment_no];
  const isBookmarked = !!bookmarkInfo;

  const cats = isBookmarked ? normCats(bookmarkInfo.category) : [];
  const firstCat = isBookmarked ? firstCatMeta(bookmarkInfo.category) : null;
  const prio = isBookmarked ? (prioMap[bookmarkInfo.priority] || prioMap.medium) : null;

  if (isBookmarked) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 ml-1.5 group cursor-pointer" data-testid={`bookmark-inline-${student.enrollment_no}`}>
            <BookmarkCheck className={`w-4 h-4 flex-shrink-0 ${bookmarkInfo.status === 'resolved' ? 'text-emerald-500 fill-emerald-500' : 'text-amber-500 fill-amber-500'}`} />
            {cats.map(cv => {
              const cm = catMap[cv] || catMap.fees_invoice;
              return <Badge key={cv} variant="outline" className={`text-[8px] px-1.5 py-0 leading-tight ${cm.color}`}>{cm.label}</Badge>;
            })}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" align="start" onClick={e => e.stopPropagation()}>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">Audit Bookmark</span>
              <Badge variant="outline" className={`text-[9px] ${prio.color}`}>{prio.label}</Badge>
            </div>
            <div className="flex flex-wrap gap-1">
              {cats.map(cv => {
                const cm = catMap[cv] || catMap.fees_invoice;
                return <Badge key={cv} variant="outline" className={`text-[9px] ${cm.color}`}>{cm.label}</Badge>;
              })}
            </div>
            <div className="text-xs text-slate-600 bg-slate-50 rounded-md px-2.5 py-2 leading-relaxed">
              <MentionText text={bookmarkInfo.issue_text || 'No description'} />
            </div>
            <div className="flex items-center justify-between text-[10px] text-slate-400">
              <span>Status: <span className={bookmarkInfo.status === 'open' ? 'text-orange-600 font-medium' : 'text-emerald-600 font-medium'}>
                {bookmarkInfo.status === 'open' ? 'Open' : 'Resolved'}
              </span></span>
              <span>{new Date(bookmarkInfo.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
            </div>
            <a href="/training/audit-bookmarks" className="inline-flex items-center gap-1 text-[10px] text-violet-600 hover:text-violet-800 hover:underline font-medium" onClick={e => e.stopPropagation()}>
              View in Audit Bookmarks <ExternalLink className="w-2.5 h-2.5" />
            </a>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      <button onClick={(e) => { e.stopPropagation(); setShowCreate(true); }} className="inline-flex items-center ml-1.5 text-slate-300 hover:text-amber-500 transition-colors" title="Bookmark for audit" data-testid={`bookmark-inline-${student.enrollment_no}`}>
        <Bookmark className="w-3.5 h-3.5" />
      </button>
      <CreateBookmarkDialog open={showCreate} onOpenChange={setShowCreate} student={student} onCreated={refresh} />
    </>
  );
}

// ── Row highlight ──
export function bookmarkRowClass(enrollmentNo, map) {
  const bm = map[enrollmentNo];
  if (!bm) return '';
  if (bm.status === 'resolved') return 'bg-emerald-50/40 border-l-2 border-l-emerald-400';
  return 'bg-amber-50/40 border-l-2 border-l-amber-400';
}

// ── Create Bookmark Dialog (multi-select categories) ──
function CreateBookmarkDialog({ open, onOpenChange, student, onCreated }) {
  const [selectedCats, setSelectedCats] = useState([]);
  const [priority, setPriority] = useState('medium');
  const [issueText, setIssueText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const toggleCat = (val) => {
    setSelectedCats(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  const handleSubmit = async () => {
    if (selectedCats.length === 0) {
      toast.error('Please select at least one category');
      return;
    }
    if (!issueText.trim()) {
      toast.error('Please describe the issue');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/training/audit-bookmarks', {
        enrollment_no: student.enrollment_no,
        student_id: student.id,
        student_name: student.name,
        course: student.course || student.course_name || '',
        category: selectedCats,
        priority,
        issue_text: issueText.trim(),
      });
      toast.success('Student bookmarked for audit');
      onOpenChange(false);
      setIssueText('');
      setSelectedCats([]);
      setPriority('medium');
      if (onCreated) onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to bookmark');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="text-base">Bookmark for Audit</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-md p-3 text-sm">
            <div className="font-medium">{student.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Enroll: {student.enrollment_no} &middot; {student.course || student.course_name || '-'}
              {student.due_amount > 0 && <span className="text-red-500 ml-1">&middot; Due: {Number(student.due_amount).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 })}</span>}
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-2 block">Category <span className="text-slate-400 font-normal">(tick one or more)</span></Label>
            <div className="grid grid-cols-2 gap-1 border rounded-md p-2 bg-slate-50/30">
              {CATEGORIES.map(c => {
                const checked = selectedCats.includes(c.value);
                return (
                  <label
                    key={c.value}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${checked ? 'bg-white shadow-sm' : 'hover:bg-white/60'}`}
                    data-testid={`bookmark-category-${c.value}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCat(c.value)}
                      className="w-3.5 h-3.5 rounded border-slate-300 text-amber-600 focus:ring-amber-500 focus:ring-offset-0 cursor-pointer"
                    />
                    <span className={`w-2 h-2 rounded-full ${c.dot} flex-shrink-0`} />
                    <span className="text-xs text-slate-700 select-none">{c.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-2 block">Priority</Label>
            <div className="flex gap-2">
              {PRIORITIES.map(p => (
                <button
                  key={p.value}
                  onClick={() => setPriority(p.value)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    priority === p.value ? p.color + ' ring-2 ring-offset-1 ring-slate-300' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`bookmark-priority-${p.value}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Issue Description *</Label>
            <Textarea value={issueText} onChange={e => setIssueText(e.target.value)} placeholder="Describe the issue or reason for audit..." className="text-sm min-h-[80px] resize-none" data-testid="bookmark-issue-text" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="text-xs" data-testid="bookmark-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={handleSubmit} disabled={submitting || selectedCats.length === 0 || !issueText.trim()} className="text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid="bookmark-submit-btn">
              {submitting ? 'Saving...' : 'Bookmark for Audit'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BookmarkButton({ student }) {
  return <BookmarkInline student={student} />;
}

export { normCats, catMap, prioMap };
export const BOOKMARK_CATEGORIES = CATEGORIES;
export const BOOKMARK_PRIORITIES = PRIORITIES;
